import { Fragment, type ReactNode } from 'react';

import { Box, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { Button } from 'Components/LegacyButton';
import RadioButton from 'Components/RadioButton';
import { Typography } from 'Components/Typography';

import { ReactComponent as MembershipQuestionMark } from 'assets/images/active-question-lime.svg';
import { ReactComponent as Cross } from 'assets/images/cross_icon.svg';

import { handleKeyPress } from 'utils/a11y';
import useResponsiveVariant from 'utils/useResponsiveVariant';

type ProductsSetCardCheckoutProps = {
  title: string;
  description: string;
  fragranceLabel: string;
  backgroundImage: {
    src: string;
    alt: string;
  };
  stickerLabel: ReactNode;
  selectedContent: {
    label: ReactNode;
    description: ReactNode;
    onClickMoreInfoModal: () => void;
  };
  onClickAdd: () => void;
  onDelete: () => void;
  productsList: Array<{
    type: string;
    label: string;
    description: Array<string>;
    capacity: string;
    image: {
      src: string;
      alt: string;
    };
  }>;
  isLoading: boolean;
  dataFrom: string;
  selected?: boolean;
  modalButtonList?: Array<{ label: string; onClick: () => void }>;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  /* use variable of ProductCardCheckout to have the same width */
  width: calc(
    var(--image-column-width) + var(--text-column-width) + var(--card-grid-column-gap) +
      var(--card-horizontal-padding) * 2
  );
  background: ${theme.colors.neutral[100]};
  box-shadow: 2px 2px 3px 0px rgba(234, 234, 234, 1);
`;

const BackgroundImageContainer = styled.div`
  position: relative;
  height: 160px;
  width: 100%;
`;

const PaddedContent = styled.div`
  padding: ${theme.spacing['6x']};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing['3x']};
`;

const Title = styled(Typography)`
  color: ${theme.colors.primary[400]};
`;

const Description = styled(Typography)`
  color: ${theme.colors.neutral[800]};
`;

const ModalButtonsSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing['3x']};
`;

const ModalButton = styled(Typography)`
  cursor: pointer;
  color: ${theme.colors.neutral[800]};
  text-decoration: underline;
`;

const FragranceLabel = styled(Typography)`
  color: ${theme.colors.neutral[800]};
`;

const CloseCardButton = styled.button`
  position: absolute;
  top: 17px;
  right: 17px;
  width: 17px;
  height: 17px;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  appearance: none;
  cursor: pointer;
`;

const ProductsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductLine = styled.div`
  display: flex;
  gap: ${theme.spacing['2x']};
  flex-direction: row;
  padding: ${theme.spacing['3x']} 0px ${theme.spacing['3x']} 0px;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const ProductLabel = styled(Typography)`
  color: ${theme.colors.primary[400]};
  font-weight: 500;
`;

const ProductDescription = styled(Typography)`
  color: ${theme.colors.neutral[800]};
`;

const LightSeparator = styled.div`
  height: 0.5px;
  background-color: ${theme.colors.primary[200]};
`;

const DarkSeparator = styled.div`
  height: 0.5px;
  background-color: ${theme.colors.primary[400]};
`;

const SelectedSectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: ${theme.spacing['5x']};
`;

const SelectedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing['2x']};
`;

const QuestionMarkButton = styled.div`
  position: relative;
  top: 3px;
  left: 6px;

  width: 22;
  height: auto;
  padding: 0;

  border: none;
  background-color: transparent;

  font: inherit;
  text-align: left;
  color: inherit;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  padding-top: ${theme.spacing['6x']};
`;

const StyledButton = styled(Button)`
  width: 108px;
  min-width: 108px;
`;

const Sticker = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  text-align: center;
  height: 80px;
  left: 30px;
  top: 40px;
  width: 80px;
  background-color: ${theme.colors.tertiary[200]};
  border-radius: 80px;
`;

const StyledRadio = styled(RadioButton)`
  padding: 0 0 0 40px;
`;

export const ProductsSetCardCheckout = ({
  title,
  description,
  backgroundImage,
  modalButtonList,
  fragranceLabel,
  selected,
  onDelete,
  productsList,
  selectedContent,
  onClickAdd,
  stickerLabel,
  isLoading,
  dataFrom,
}: ProductsSetCardCheckoutProps) => {
  const { isMobile } = useResponsiveVariant();
  return (
    <Root data-testid="product-set-card">
      <BackgroundImageContainer>
        <Image alt={backgroundImage.alt} fill objectFit="cover" src={backgroundImage.src} />
        <Sticker>{stickerLabel}</Sticker>
        {selected && (
          <CloseCardButton
            aria-label="close"
            data-click="remove"
            data-from={dataFrom}
            onClick={() => !isLoading && onDelete()}
            type="button"
          >
            <Cross height={24} width={24} />
          </CloseCardButton>
        )}
      </BackgroundImageContainer>
      <PaddedContent>
        <Header>
          <Title variant="h3">{title}</Title>

          <Description variant="p2">{description}</Description>
          {modalButtonList && (
            <ModalButtonsSection>
              {modalButtonList.map(({ label, onClick }) => (
                <ModalButton
                  key={label}
                  onClick={onClick}
                  onKeyPress={handleKeyPress(() => onClick())}
                  role="button"
                  tabIndex={0}
                  variant="p3"
                >
                  {label}
                </ModalButton>
              ))}

              <FragranceLabel variant="p3">{fragranceLabel}</FragranceLabel>
            </ModalButtonsSection>
          )}
        </Header>
        <ProductsList>
          {productsList.map((product, index) => (
            <Fragment key={product.type}>
              <ProductLine>
                <Image alt={product.image.alt} src={product.image.src} width={67} />
                <ProductInfo>
                  <ProductLabel variant="p3">{`${product.label} — ${product.capacity}`}</ProductLabel>

                  <ProductDescription variant="p3">{product.description[0]}</ProductDescription>
                </ProductInfo>
              </ProductLine>
              {productsList.length - 1 !== index && <LightSeparator />}
            </Fragment>
          ))}
        </ProductsList>
        <DarkSeparator />
        {selected ? (
          <SelectedSectionContainer>
            <StyledRadio onClick={() => {}} selected>
              <SelectedContent>
                <Box
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  display="flex"
                  flexDirection="row"
                  gap="3x"
                >
                  <Title variant="p3">{selectedContent.label}</Title>
                  <QuestionMarkButton
                    aria-label="Open membership modal"
                    onClick={selectedContent.onClickMoreInfoModal}
                    onKeyPress={selectedContent.onClickMoreInfoModal}
                    role="button"
                    tabIndex={0}
                  >
                    <MembershipQuestionMark
                      style={{ '--fill-color': theme.colors.highlight[200] }}
                    />
                  </QuestionMarkButton>
                </Box>

                <ProductDescription variant="p3">{selectedContent.description}</ProductDescription>
              </SelectedContent>
            </StyledRadio>
          </SelectedSectionContainer>
        ) : (
          <ButtonContainer>
            <StyledButton
              data-click="add"
              data-from={dataFrom}
              data-testid="product-set-card-add-button"
              disabled={isLoading}
              isLoading={isLoading}
              onClick={onClickAdd}
              variant="vert"
            >
              ADD
            </StyledButton>
          </ButtonContainer>
        )}
      </PaddedContent>
    </Root>
  );
};
